import React, { useState, useMemo } from 'react';
import { SuccessStoryModel, SUBJECT_TYPES, TERM_TYPES } from '../../models';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { TrendingUp, BookOpen, BarChart3, Filter, RotateCcw } from 'lucide-react';

interface Props {
    stories: SuccessStoryModel[];
    isDarkMode?: boolean;
}

export const SuccessAnalyticsChart: React.FC<Props> = ({ stories, isDarkMode = false }) => {
    const [selectedYear, setSelectedYear] = useState<string>('전체');
    const [selectedSemester, setSelectedSemester] = useState<string>('전체');
    const [selectedSubject, setSelectedSubject] = useState<string>('전체');

    // Available years extracted dynamically
    const availableYears = useMemo(() => {
        return Array.from(
            new Set(
                stories
                    .map(s => s.year)
                    .filter((y): y is string => Boolean(y && y.trim()))
            )
        ).sort((a, b) => Number(a) - Number(b));
    }, [stories]);

    // Flexible matching helpers to handle variations (e.g., '1학기 기말' vs '1학기 기말고사', '2026' vs '2026년')
    const isYearMatch = (yearInDb?: string, selectedYear?: string) => {
        if (!selectedYear || selectedYear === '전체') return true;
        if (!yearInDb) return false;
        const dbY = yearInDb.toString().replace(/년|\s+/g, '');
        const selY = selectedYear.toString().replace(/년|\s+/g, '');
        return dbY === selY;
    };

    const isSemesterMatch = (semesterInDb?: string, selectedSemester?: string) => {
        if (!selectedSemester || selectedSemester === '전체') return true;
        if (!semesterInDb) return false;
        const dbS = semesterInDb.toString().replace(/고사|\s+/g, '');
        const selS = selectedSemester.toString().replace(/고사|\s+/g, '');
        return dbS === selS || dbS.includes(selS) || selS.includes(dbS);
    };

    const isSubjectMatch = (subjectInDb?: string, selectedSubject?: string) => {
        if (!selectedSubject || selectedSubject === '전체') return true;
        if (!subjectInDb) return false;
        return subjectInDb.trim() === selectedSubject.trim();
    };

    // Filter stories based on selected Year, Semester, Subject
    const filteredStories = useMemo(() => {
        return stories.filter(s => {
            const matchesYear = isYearMatch(s.year, selectedYear);
            const matchesSemester = isSemesterMatch(s.semester, selectedSemester);
            const matchesSubject = isSubjectMatch(s.subject, selectedSubject);
            return matchesYear && matchesSemester && matchesSubject;
        });
    }, [stories, selectedYear, selectedSemester, selectedSubject]);

    // Metrics calculation
    const metrics = useMemo(() => {
        const total = filteredStories.length;
        
        const numericScores = filteredStories
            .map(s => parseFloat((s.score || '').replace(/[^0-9.]/g, '')))
            .filter(score => !isNaN(score) && score > 0);
        
        const avgScore = numericScores.length > 0
            ? (numericScores.reduce((acc, curr) => acc + curr, 0) / numericScores.length).toFixed(1)
            : '0.0';

        const perfectScores = filteredStories.filter(s => {
            const val = (s.score || s.rank || s.standing || '').toString();
            return val.includes('100') || val === '1' || val.includes('1등') || val.includes('1등급');
        }).length;

        // Top subject
        const subjectCounts: Record<string, number> = {};
        filteredStories.forEach(s => {
            if (s.subject) {
                subjectCounts[s.subject] = (subjectCounts[s.subject] || 0) + 1;
            }
        });
        const topSubject = Object.entries(subjectCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

        return { total, avgScore, perfectScores, topSubject };
    }, [filteredStories]);

    // 1. Semester & Exam Trend Data
    const examTrendData = useMemo(() => {
        const examMap: Record<string, { exam: string; totalCount: number; scores: number[]; mathScores: number[]; korScores: number[]; engScores: number[]; sciScores: number[] }> = {};

        filteredStories.forEach(s => {
            if (!s.year || !s.semester) return;
            const key = `${s.year}년 ${s.semester}`;
            if (!examMap[key]) {
                examMap[key] = {
                    exam: key,
                    totalCount: 0,
                    scores: [],
                    mathScores: [],
                    korScores: [],
                    engScores: [],
                    sciScores: []
                };
            }
            examMap[key].totalCount += 1;
            
            const numScore = parseFloat((s.score || '').replace(/[^0-9.]/g, ''));
            if (!isNaN(numScore) && numScore > 0) {
                examMap[key].scores.push(numScore);
                if (s.subject === '수학') examMap[key].mathScores.push(numScore);
                if (s.subject === '국어') examMap[key].korScores.push(numScore);
                if (s.subject === '영어') examMap[key].engScores.push(numScore);
                if (s.subject === '과학') examMap[key].sciScores.push(numScore);
            }
        });

        return Object.values(examMap).map(item => ({
            exam: item.exam,
            '실적 건수': item.totalCount,
            '평균 점수': item.scores.length > 0 ? parseFloat((item.scores.reduce((a, b) => a + b, 0) / item.scores.length).toFixed(1)) : 0,
            '수학 평균': item.mathScores.length > 0 ? parseFloat((item.mathScores.reduce((a, b) => a + b, 0) / item.mathScores.length).toFixed(1)) : undefined,
            '국어 평균': item.korScores.length > 0 ? parseFloat((item.korScores.reduce((a, b) => a + b, 0) / item.korScores.length).toFixed(1)) : undefined,
            '영어 평균': item.engScores.length > 0 ? parseFloat((item.engScores.reduce((a, b) => a + b, 0) / item.engScores.length).toFixed(1)) : undefined,
            '과학 평균': item.sciScores.length > 0 ? parseFloat((item.sciScores.reduce((a, b) => a + b, 0) / item.sciScores.length).toFixed(1)) : undefined,
        }));
    }, [filteredStories]);

    // 2. Subject Breakdown Data
    const subjectBreakdownData = useMemo(() => {
        const subjects = ['국어', '영어', '수학', '과학'];
        return subjects.map(sub => {
            const subStories = filteredStories.filter(s => s.subject === sub);
            const numScores = subStories
                .map(s => parseFloat((s.score || '').replace(/[^0-9.]/g, '')))
                .filter(score => !isNaN(score) && score > 0);
            
            const avg = numScores.length > 0
                ? parseFloat((numScores.reduce((a, b) => a + b, 0) / numScores.length).toFixed(1))
                : 0;

            return {
                과목: sub,
                '실적 건수': subStories.length,
                '평균 점수': avg,
            };
        });
    }, [filteredStories]);

    // Colors
    const COLORS = {
        bg: isDarkMode ? 'bg-slate-900/90 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900',
        cardBg: isDarkMode ? 'bg-slate-800/80 border-slate-700/80' : 'bg-slate-50 border-slate-100',
        textMuted: isDarkMode ? 'text-slate-400' : 'text-slate-500',
        grid: isDarkMode ? '#334155' : '#e2e8f0',
        tooltipBg: isDarkMode ? '#0f172a' : '#ffffff',
        tooltipBorder: isDarkMode ? '#334155' : '#cbd5e1',
        tooltipText: isDarkMode ? '#f8fafc' : '#0f172a',
    };

    return (
        <div className={`p-6 rounded-3xl border ${COLORS.bg} space-y-6 animate-in fade-in duration-500`}>
            {/* Header & Filter Controls */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-700/40">
                <div className="flex items-center gap-2.5">
                    <div className="p-2.5 rounded-2xl bg-indigo-600/10 text-indigo-500 border border-indigo-500/20">
                        <BarChart3 size={20} />
                    </div>
                    <div>
                        <h3 className="text-lg font-black tracking-tight">성적 데이터 정밀 분석 그래프</h3>
                        <p className={`text-xs font-medium ${COLORS.textMuted}`}>연도, 학기, 과목별 종합 실적 성취 추이를 한눈에 확인하세요.</p>
                    </div>
                </div>

                {/* Filter Controls */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-400 mr-1">
                        <Filter size={14} className="text-indigo-400" />
                        <span>통계 필터</span>
                    </div>

                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        className={`px-3 py-1.5 rounded-xl border text-xs font-bold outline-none cursor-pointer ${isDarkMode ? 'bg-slate-950 border-slate-800 text-slate-200' : 'bg-slate-100 border-slate-200 text-slate-800'}`}
                    >
                        <option value="전체">전체 연도</option>
                        {availableYears.map(yr => (
                            <option key={yr} value={yr}>{yr}년</option>
                        ))}
                    </select>

                    <select
                        value={selectedSemester}
                        onChange={(e) => setSelectedSemester(e.target.value)}
                        className={`px-3 py-1.5 rounded-xl border text-xs font-bold outline-none cursor-pointer ${isDarkMode ? 'bg-slate-950 border-slate-800 text-slate-200' : 'bg-slate-100 border-slate-200 text-slate-800'}`}
                    >
                        <option value="전체">전체 학기/시험</option>
                        {TERM_TYPES.map(term => (
                            <option key={term} value={term}>{term}</option>
                        ))}
                    </select>

                    <select
                        value={selectedSubject}
                        onChange={(e) => setSelectedSubject(e.target.value)}
                        className={`px-3 py-1.5 rounded-xl border text-xs font-bold outline-none cursor-pointer ${isDarkMode ? 'bg-slate-950 border-slate-800 text-slate-200' : 'bg-slate-100 border-slate-200 text-slate-800'}`}
                    >
                        <option value="전체">전체 과목</option>
                        {SUBJECT_TYPES.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                        ))}
                    </select>

                    {(selectedYear !== '전체' || selectedSemester !== '전체' || selectedSubject !== '전체') && (
                        <button
                            onClick={() => { setSelectedYear('전체'); setSelectedSemester('전체'); setSelectedSubject('전체'); }}
                            className="p-1.5 rounded-lg text-indigo-400 hover:text-indigo-300 transition-colors"
                            title="필터 초기화"
                        >
                            <RotateCcw size={14} />
                        </button>
                    )}
                </div>
            </div>

            {/* Top Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-4 rounded-2xl border ${COLORS.cardBg} space-y-1`}>
                    <p className={`text-xs font-bold ${COLORS.textMuted}`}>총 성적 실적 건수</p>
                    <p className="text-2xl font-black text-indigo-500">{metrics.total}<span className="text-xs font-medium ml-1 text-slate-400">건</span></p>
                </div>
                <div className={`p-4 rounded-2xl border ${COLORS.cardBg} space-y-1`}>
                    <p className={`text-xs font-bold ${COLORS.textMuted}`}>평균 성적</p>
                    <p className="text-2xl font-black text-emerald-500">{metrics.avgScore}<span className="text-xs font-medium ml-1 text-slate-400">점</span></p>
                </div>
                <div className={`p-4 rounded-2xl border ${COLORS.cardBg} space-y-1`}>
                    <p className={`text-xs font-bold ${COLORS.textMuted}`}>100점 / 1등급 / 1등</p>
                    <p className="text-2xl font-black text-amber-500">{metrics.perfectScores}<span className="text-xs font-medium ml-1 text-slate-400">건</span></p>
                </div>
                <div className={`p-4 rounded-2xl border ${COLORS.cardBg} space-y-1`}>
                    <p className={`text-xs font-bold ${COLORS.textMuted}`}>최다 배출 과목</p>
                    <p className="text-2xl font-black text-violet-500">{metrics.topSubject}</p>
                </div>
            </div>

            {/* Graphs Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2">
                
                {/* Graph 1: Exam Trend Line & Bar Chart */}
                <div className={`p-5 rounded-2xl border ${COLORS.cardBg} space-y-4`}>
                    <div className="flex justify-between items-center">
                        <h4 className="text-sm font-black flex items-center gap-1.5">
                            <TrendingUp size={16} className="text-indigo-500" />
                            연도/학기별 평균 점수 추이
                        </h4>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                            시험별 분석
                        </span>
                    </div>

                    {examTrendData.length === 0 ? (
                        <div className="h-64 flex items-center justify-center text-xs text-slate-500 font-medium">
                            선택된 조건의 시험별 성적 데이터가 없습니다.
                        </div>
                    ) : (
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={examTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
                                    <XAxis dataKey="exam" tick={{ fontSize: 10, fill: isDarkMode ? '#94a3b8' : '#64748b' }} />
                                    <YAxis domain={[60, 100]} tick={{ fontSize: 10, fill: isDarkMode ? '#94a3b8' : '#64748b' }} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: COLORS.tooltipBg,
                                            borderColor: COLORS.tooltipBorder,
                                            borderRadius: '12px',
                                            color: COLORS.tooltipText,
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                                    <Line type="monotone" dataKey="평균 점수" stroke="#6366f1" strokeWidth={3} dot={{ r: 5, fill: '#6366f1' }} activeDot={{ r: 7 }} />
                                    <Line type="monotone" dataKey="수학 평균" stroke="#10b981" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="국어 평균" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="영어 평균" stroke="#ec4899" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                {/* Graph 2: Subject Breakdown Bar Chart */}
                <div className={`p-5 rounded-2xl border ${COLORS.cardBg} space-y-4`}>
                    <div className="flex justify-between items-center">
                        <h4 className="text-sm font-black flex items-center gap-1.5">
                            <BookOpen size={16} className="text-violet-500" />
                            과목별 평균 점수 & 실적 분포
                        </h4>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20">
                            과목별 분석
                        </span>
                    </div>

                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={subjectBreakdownData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
                                <XAxis dataKey="과목" tick={{ fontSize: 11, fontWeight: 'bold', fill: isDarkMode ? '#94a3b8' : '#64748b' }} />
                                <YAxis yAxisId="left" orientation="left" domain={[0, 100]} tick={{ fontSize: 10, fill: isDarkMode ? '#94a3b8' : '#64748b' }} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: isDarkMode ? '#94a3b8' : '#64748b' }} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: COLORS.tooltipBg,
                                        borderColor: COLORS.tooltipBorder,
                                        borderRadius: '12px',
                                        color: COLORS.tooltipText,
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                                <Bar yAxisId="left" dataKey="평균 점수" fill="#8b5cf6" radius={[6, 6, 0, 0]} barSize={28}>
                                    {subjectBreakdownData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={['#6366f1', '#10b981', '#f59e0b', '#ec4899'][index % 4]} />
                                    ))}
                                </Bar>
                                <Bar yAxisId="right" dataKey="실적 건수" fill="#38bdf8" radius={[6, 6, 0, 0]} barSize={16} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

            </div>
        </div>
    );
};
